import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { MdMenu } from 'react-icons/md';
import './Navbar.css';

const ROLE_LABELS = {
  examiner: 'Examiner',
  subject_matter_expert: 'SME',
  moderator: 'Moderator',
  cetcoordinator: 'CET Coordinator',
  cetadmin: 'CET Admin',
  superadmin: 'Super Admin',
  coordinator: 'Coordinator',
};
const roleLabel = (r) => ROLE_LABELS[r] || r;

const Navbar = () => {
  const { user, logout, switchRole } = useContext(AuthContext);
  const navigate = useNavigate();
  // Track avatar load failures so a broken/404 image falls back to the SVG icon.
  const [avatarError, setAvatarError] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSwitch = async (role) => {
    if (switching || role === user.role) { setRoleMenuOpen(false); return; }
    try {
      setSwitching(true);
      await switchRole(role);
      setRoleMenuOpen(false);
      // Hard reload so the sidebar, permissions and dashboard fully re-initialise
      // for the newly-active role (same approach as impersonation).
      window.location.href = '/';
    } catch (e) {
      // stay put; surfaced via the disabled state resetting
    } finally {
      setSwitching(false);
    }
  };

  const initials = (user?.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  // Opens the sidebar drawer (mobile / tablet) or restores a hidden sidebar.
  const openNavigation = () => {
    window.dispatchEvent(new Event('app:toggle-sidebar'));
  };

  return (
    <nav className={`navbar ${user ? 'with-sidebar' : ''}`}>
      <div className="navbar-brand">
        {user && (
          <button
            type="button"
            className="navbar-menu-toggle"
            onClick={openNavigation}
            aria-label="Open navigation"
          >
            <MdMenu aria-hidden="true" />
            <span>Menu</span>
          </button>
        )}
        {user ? (
          <div className="welcome-text">
            <div className="welcome-icon">
              {user.avatar_url && !avatarError ? (
                <img
                  src={`${process.env.REACT_APP_USE_PRODUCTION === 'true' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL}${user.avatar_url}`}
                  alt={user.name}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                initials ? (
                  <span className="welcome-initials">{initials}</span>
                ) : (
                  <svg className="nav-svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )
              )}
            </div>
            <span>Welcome, {user.name}</span>
          </div>
        ) : (
          <Link to="/" className="brand-link">
            <span className="brand-text">Question Generator</span>
          </Link>
        )}
      </div>

      <div className="navbar-menu">
        {user && Array.isArray(user.roles) && user.roles.length > 1 && (
          <div className="role-switcher">
            <button
              type="button"
              className="btn-navbar btn-navbar-secondary role-switcher-trigger"
              onClick={() => setRoleMenuOpen((o) => !o)}
              disabled={switching}
              title="Switch active role"
            >
              <svg className="nav-svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              <span className="role-switcher-caption">Role</span>
              <span className="role-switcher-value">{switching ? 'Switching…' : roleLabel(user.role)}</span>
              <span className={`role-switcher-caret ${roleMenuOpen ? 'open' : ''}`} aria-hidden="true" />
            </button>
            {roleMenuOpen && (
              <>
                <div className="role-switcher-scrim" onClick={() => setRoleMenuOpen(false)} />
                <div className="role-switcher-menu">
                  <div className="role-switcher-heading">
                    Switch role
                  </div>
                  {user.roles.map((r) => {
                    const active = r.role === user.role;
                    return (
                      <button
                        key={r.role}
                        type="button"
                        disabled={switching}
                        onClick={() => handleSwitch(r.role)}
                        className={`role-switcher-option ${active ? 'active' : ''}`}
                      >
                        <span>{roleLabel(r.role)}</span>
                        {r.is_primary
                          ? <span className="role-switcher-tag">Primary</span>
                          : (active ? <span className="role-switcher-tag role-switcher-tag-current">Current</span> : null)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
        {user ? (
          <button onClick={handleLogout} className="btn-navbar btn-navbar-logout">
            <svg className="nav-svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Logout</span>
          </button>
        ) : (
          <>
            <Link to="/login" className="btn-navbar btn-navbar-secondary">
              <svg className="nav-svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              <span>Login</span>
            </Link>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
